// src/components/mitchRoom/modelOutputs/PlayerAccuracyTable.js
import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { cn } from 'lib/utils';
import { getLogo, getPositionLabel } from './utils';
import { UI, RETURN_PREDICTION_THRESHOLD, BLANK_PREDICTION_THRESHOLD } from '../../../utils/constants';

// Width of the sticky player column
const STICKY_COL = 160;

const SORT_ACCESSORS = {
  fixtures: (p) => p.totalFixtures,
  return: (p) => (p.returnPredictions > 0 ? p.returnAccuracy : null),
  blank: (p) => (p.blankPredictions > 0 ? p.blankAccuracy : null),
  start: (p) => p.startAccuracy,
  play: (p) => p.playAccuracy,
  mae: (p) => p.minutesMAE,
};

// Accuracy tier dot: green >= 70%, amber >= 50%, red below
function tierDotClass(accuracy) {
  if (accuracy >= 70) return 'bg-success';
  if (accuracy >= 50) return 'bg-warning';
  return 'bg-destructive';
}

function AccuracyCell({ correct, total, accuracy }) {
  if (!total || accuracy == null) {
    return <span className="text-muted-foreground">-</span>;
  }
  return (
    <span className="inline-flex items-center justify-end gap-1.5 whitespace-nowrap">
      <span className="text-token-sm text-muted-foreground">
        {correct}/{total}
      </span>
      <span className="font-semibold">{accuracy.toFixed(1)}%</span>
      <span className={cn('inline-block h-2 w-2 rounded-full', tierDotClass(accuracy))} />
    </span>
  );
}

function SortableTh({ label, sortKey, activeKey, dir, onSort }) {
  const isActive = activeKey === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      aria-sort={isActive ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="sticky top-0 z-sticky-header cursor-pointer select-none whitespace-nowrap border-b border-borderGrey bg-white p-2 text-center font-semibold hover:bg-neutral-50"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
      </span>
    </th>
  );
}

export default function PlayerAccuracyTable({
  playerAccuracy: rawPredictions,
  teamPredictions,
  teamMap,
  elementMap,
  selectedTeam,
  selectedSeason,
  startingThreshold,
  playingThreshold,
}) {
  // Client-side sort — default: most fixtures first (matching legacy behaviour)
  const [sortKey, setSortKey] = useState('fixtures');
  const [sortDir, setSortDir] = useState('desc');

  // Exclude players with too few fixtures from distorting the accuracy picture
  const [minFixtures, setMinFixtures] = useState(0);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // Build fixture lookup for season filtering (fixture_optaID + club_optaID -> compSeasonID)
  const fixtureSeasonMap = useMemo(() => {
    if (!teamPredictions) return new Map();
    const map = new Map();
    teamPredictions.forEach((f) => {
      const key = `${f.fixture_optaID}_${f.club_optaID}`;
      map.set(key, f.compSeasonID);
    });
    return map;
  }, [teamPredictions]);

  // Process raw predictions into player accuracy summaries
  const playerAccuracyData = useMemo(() => {
    if (!rawPredictions || rawPredictions.length === 0) return [];

    // Group by player - only include players that are in elementMap
    const playerGroups = new Map();

    rawPredictions.forEach((result) => {
      // Only include players that are in elementMap (current season players)
      const playerInfo = elementMap?.get(result.player_optaID);
      if (!playerInfo) return;

      // Filter by team if selected
      if (selectedTeam && result.club_optaID !== selectedTeam) return;

      // Only include fixtures that exist in teamPredictions (have season data)
      const fixtureKey = `${result.fixture_optaID}_${result.club_optaID}`;
      const fixtureSeasonID = fixtureSeasonMap.get(fixtureKey);
      if (!fixtureSeasonID) return; // Skip fixtures not in teamPredictions

      // Filter by season if selected
      if (selectedSeason && fixtureSeasonID !== selectedSeason) return;

      if (!playerGroups.has(result.player_optaID)) {
        // Get team info from teamMap using club_optaID
        const teamInfo = teamMap?.get(result.club_optaID);
        playerGroups.set(result.player_optaID, {
          playerName: playerInfo.web_name || result.player_optaID,
          elementType: playerInfo.element_type,
          clubOptaID: result.club_optaID,
          teamFplID: playerInfo.team_fplID,
          club: teamInfo?.name || result.club,
          shortName: teamInfo?.short_name || result.club,
          fixtures: [],
        });
      }

      playerGroups.get(result.player_optaID).fixtures.push(result);
    });

    const returnThreshold = RETURN_PREDICTION_THRESHOLD || 4;
    const blankThreshold = BLANK_PREDICTION_THRESHOLD || 4;
    const startThresholdDecimal = startingThreshold / 100;
    const playThresholdDecimal = playingThreshold / 100;

    // Calculate accuracy metrics
    return Array.from(playerGroups.values())
      .map((group) => {
        const totalFixtures = group.fixtures.length;

        // Return predictions: when we predict >= threshold points
        const returnPredictions = group.fixtures.filter((f) => f.expected_points >= returnThreshold);
        const correctReturnPredictions = returnPredictions.filter(
          (f) => f.actual_points != null && f.actual_points >= returnThreshold
        );
        const returnAccuracy =
          returnPredictions.length > 0
            ? (correctReturnPredictions.length / returnPredictions.length) * 100
            : 0;

        // Blank predictions: when we predict < threshold points
        const blankPredictions = group.fixtures.filter((f) => f.expected_points < blankThreshold);
        const correctBlankPredictions = blankPredictions.filter(
          (f) => f.actual_points != null && f.actual_points < blankThreshold
        );
        const blankAccuracy =
          blankPredictions.length > 0
            ? (correctBlankPredictions.length / blankPredictions.length) * 100
            : 0;

        // Starting predictions accuracy (using configurable threshold)
        const startPredictions = group.fixtures.filter(
          (f) => f.prob_starting != null && f.actual_started != null
        );
        const correctStartPredictions = startPredictions.filter((f) => {
          const predictedStarting = f.prob_starting >= startThresholdDecimal;
          return (
            (predictedStarting && f.actual_started === 1) ||
            (!predictedStarting && f.actual_started === 0)
          );
        });
        const startAccuracy =
          startPredictions.length > 0
            ? (correctStartPredictions.length / startPredictions.length) * 100
            : null;

        // Playing predictions accuracy (using configurable threshold)
        const playPredictions = group.fixtures.filter(
          (f) => f.prob_playing != null && f.actual_played != null
        );
        const correctPlayPredictions = playPredictions.filter((f) => {
          const predictedPlaying = f.prob_playing >= playThresholdDecimal;
          return (
            (predictedPlaying && f.actual_played === 1) ||
            (!predictedPlaying && f.actual_played === 0)
          );
        });
        const playAccuracy =
          playPredictions.length > 0
            ? (correctPlayPredictions.length / playPredictions.length) * 100
            : null;

        // Minutes MAE (Mean Absolute Error)
        const minutesPredictions = group.fixtures.filter(
          (f) => f.expected_minutes != null && f.actual_minutes != null
        );
        const minutesMAE =
          minutesPredictions.length > 0
            ? minutesPredictions.reduce(
                (acc, f) => acc + Math.abs(f.expected_minutes - f.actual_minutes),
                0
              ) / minutesPredictions.length
            : null;

        return {
          playerName: group.playerName,
          elementType: group.elementType,
          team: { name: group.club, short_name: group.shortName, fplID: group.teamFplID },
          totalFixtures,
          returnPredictions: returnPredictions.length,
          correctReturnPredictions: correctReturnPredictions.length,
          returnAccuracy,
          blankPredictions: blankPredictions.length,
          correctBlankPredictions: correctBlankPredictions.length,
          blankAccuracy,
          startPredictions: startPredictions.length,
          correctStartPredictions: correctStartPredictions.length,
          startAccuracy,
          playPredictions: playPredictions.length,
          correctPlayPredictions: correctPlayPredictions.length,
          playAccuracy,
          minutesPredictions: minutesPredictions.length,
          minutesMAE,
        };
      })
      .filter((p) => p.totalFixtures > 0);
  }, [rawPredictions, fixtureSeasonMap, teamMap, elementMap, selectedTeam, selectedSeason, startingThreshold, playingThreshold]);

  const visibleRows = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey] || SORT_ACCESSORS.fixtures;
    const dir = sortDir === 'asc' ? 1 : -1;
    return playerAccuracyData
      .filter((p) => p.totalFixtures >= (minFixtures || 0))
      .sort((a, b) => {
        const av = accessor(a);
        const bv = accessor(b);
        if (av == null && bv == null) return 0;
        if (av == null) return 1; // nulls last regardless of direction
        if (bv == null) return -1;
        return (av - bv) * dir;
      });
  }, [playerAccuracyData, sortKey, sortDir, minFixtures]);

  if (playerAccuracyData.length === 0) {
    return (
      <div className="p-6 text-center text-token-md text-muted-foreground">
        No player data found. Try selecting a different team or adjusting filters.
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-token-xl font-semibold">Player Prediction Accuracy Summary</h3>
      <p className="mb-3 text-token-base text-muted-foreground">
        Return predictions: when we predict ≥4 pts. Blank predictions: when we predict &lt;4 pts.
        Starting/Playing thresholds are configurable above. Click a column header to sort.
      </p>

      <div className="mb-3 flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <Label className="text-token-base font-normal text-muted-foreground">Min fixtures</Label>
          <Input
            type="number"
            min={0}
            value={minFixtures}
            onChange={(e) => setMinFixtures(Math.max(0, parseInt(e.target.value) || 0))}
            className="w-[100px]"
          />
        </div>
        <span className="pb-2 text-token-base text-muted-foreground">
          {visibleRows.length} of {playerAccuracyData.length} players shown
        </span>
      </div>

      <div
        className="overflow-auto rounded-token-large border border-borderGrey bg-white"
        style={{ maxHeight: UI.height.table.large }}
      >
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th
                className="sticky left-0 top-0 z-[4] border-b border-borderGrey bg-white p-2 text-left font-semibold"
                style={{ minWidth: STICKY_COL }}
              >
                Player
              </th>
              <th className="sticky top-0 z-sticky-header whitespace-nowrap border-b border-borderGrey bg-white p-2 text-left font-semibold">
                Pos
              </th>
              <SortableTh label="Fixtures" sortKey="fixtures" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
              <SortableTh label="Return Acc." sortKey="return" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
              <SortableTh label="Blank Acc." sortKey="blank" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
              <SortableTh
                label={`Starting Acc. (${startingThreshold}%)`}
                sortKey="start"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
              />
              <SortableTh
                label={`Playing Acc. (${playingThreshold}%)`}
                sortKey="play"
                activeKey={sortKey}
                dir={sortDir}
                onSort={handleSort}
              />
              <SortableTh label="Mins MAE" sortKey="mae" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((item, idx) => {
              const teamLogo = item.team ? getLogo(item.team.name) : null;

              return (
                <tr key={idx} className="group hover:bg-neutral-50">
                  {/* Player with team logo - sticky */}
                  <td
                    className="sticky left-0 z-sticky-column border-b border-borderGrey bg-white p-2 transition-colors group-hover:bg-neutral-50"
                    style={{ minWidth: STICKY_COL }}
                  >
                    <div className="flex items-center gap-2">
                      {teamLogo && (
                        <img
                          src={teamLogo}
                          alt={item.team?.short_name}
                          className="h-5 w-5 object-contain"
                        />
                      )}
                      <div>
                        <p className="leading-tight">{item.playerName || '-'}</p>
                        <p className="text-token-sm leading-tight text-muted-foreground">
                          {item.team?.short_name || '-'}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Position */}
                  <td className="border-b border-borderGrey p-2">
                    <Badge variant="outline">{getPositionLabel(item.elementType)}</Badge>
                  </td>

                  {/* Total Fixtures */}
                  <td className="border-b border-borderGrey p-2 text-center font-medium">
                    {item.totalFixtures}
                  </td>

                  {/* Return Predictions */}
                  <td className="border-b border-borderGrey p-2 text-center">
                    <AccuracyCell
                      correct={item.correctReturnPredictions}
                      total={item.returnPredictions}
                      accuracy={item.returnPredictions > 0 ? item.returnAccuracy : null}
                    />
                  </td>

                  {/* Blank Predictions */}
                  <td className="border-b border-borderGrey p-2 text-center">
                    <AccuracyCell
                      correct={item.correctBlankPredictions}
                      total={item.blankPredictions}
                      accuracy={item.blankPredictions > 0 ? item.blankAccuracy : null}
                    />
                  </td>

                  {/* Starting Predictions Accuracy */}
                  <td className="border-b border-borderGrey p-2 text-center">
                    <AccuracyCell
                      correct={item.correctStartPredictions}
                      total={item.startPredictions}
                      accuracy={item.startAccuracy}
                    />
                  </td>

                  {/* Playing Predictions Accuracy */}
                  <td className="border-b border-borderGrey p-2 text-center">
                    <AccuracyCell
                      correct={item.correctPlayPredictions}
                      total={item.playPredictions}
                      accuracy={item.playAccuracy}
                    />
                  </td>

                  {/* Minutes MAE */}
                  <td className="border-b border-borderGrey p-2 text-center">
                    {item.minutesPredictions > 0 && item.minutesMAE != null ? (
                      <span
                        className={cn(
                          'font-medium',
                          item.minutesMAE <= 15
                            ? 'text-success'
                            : item.minutesMAE <= 25
                              ? 'text-warning'
                              : 'text-destructive'
                        )}
                      >
                        {item.minutesMAE.toFixed(1)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
